<template>
	<div class="key-benefit-analysis">
		<div class="analysis-header">
			<div class="selector">
				<div class="driver-selector-dropdown" :class="{ 'is-active': isDropdownOpen }">
					<div class="dropdown-trigger" @click="toggleDropdown" aria-haspopup="true" aria-controls="dropdown-menu">
						<span>{{ t('dashboard_geo.key_benefit.select') }}</span>
					</div>
					<div class="dropdown-menu" id="dropdown-menu" role="menu">
						<div class="dropdown-content">
							<p>{{ t('dashboard_geo.key_benefit.max_select') }}</p>
							<div class="dropdown-items">
								<label class="dropdown-item checkbox" v-for="item in availableItems" :key="item.id">
									<CustomCheckBox
										type="checkbox"
										:name="`driver-${item.id}`"
										:checked="selectedDriverIds.includes(item.id)"
										@change="e => handleDriverSelection(e, item.id)"
										:rounded="false"
										:disabled="selectedDriverIds.length >= maxSelections && !selectedDriverIds.includes(item.id)"
									/>
									<span class="dropdown-item-label">{{ item.name }}</span>
								</label>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

	<div class="chart-grid" v-if="selectedDrivers.some(d => hasData(d))">
		<div class="chart-card" v-for="driver in selectedDrivers.filter(d => hasData(d))" :key="driver.id">
				<div class="chart-header">
					<h4 class="chart-title">{{ driver.name }}</h4>
					<div class="driver-filter">
						<div class="driver-label-dropdown" :class="{ 'is-active': openDriverFilterId === driver.id }">
							<button class="driver-label-trigger" type="button" @click.stop="toggleDriverLabelDropdown(driver.id)">
								{{ getDriverLabelSummary(driver) }}
							</button>
							<div class="driver-label-menu" @click.stop>
								<p v-if="getDriverLabelOptions(driver).length === 0" class="driver-label-helper">{{ t('dashboard_geo.key_benefit.no_labels_available') }}</p>
								<div v-else class="driver-label-options">
									<label class="driver-label-checkbox" v-for="item in getDriverLabelOptions(driver)" :key="`driver-${driver.id}-label-${item.id}`">
										<CustomCheckBox type="checkbox" :name="`driver-${driver.id}-label-${item.id}`" :checked="isDriverLabelSelected(driver.id, item.id)" @change="e => handleDriverLabelSelection(e, driver.id, item.id)" :rounded="false" />
										<span class="driver-label-text">{{ item.name }}</span>
									</label>
								</div>
								<button v-if="getSelectedDriverLabelIds(driver.id).length > 0" class="driver-label-clear" type="button" @click="clearDriverLabelSelection(driver.id)">{{ t('clear') }}</button>
							</div>
						</div>
					</div>
				</div>
				<div v-if="hasData(driver)" class="chart-content">
					<div class="chart-wrapper">
						<div class="chart-canvas">
							<RoundSpiderChart
								:key="`chart-${driver.id}`"
								:actors="getSortedActors(driver, getFilteredDriver(driver)?.actors || [])"
								:scores="getSpiderScores(driver)"
								:previous-scores="getSpiderPreviousScores(driver)"
								:item-name="driver.name"
								:show-previous-polygon="false"
								:show-tooltip="false"
								color="var(--bleu-200)"
								:size="280"
								:point-size="0"
								@polygon-enter="hoveredDriverId = driver.id"
								@polygon-leave="hoveredDriverId = null"
							/>
						</div>
						<div v-if="hoveredDriverId === driver.id" class="kb-hover-tooltip">
							<div class="kb-hover-tooltip-title">{{ driver.name }} : {{ Number(getFilteredDriver(driver)?.total_occurrences ?? 0) }}</div>
							<div v-for="actor in getSortedActors(driver, getFilteredDriver(driver)?.actors)" :key="`tooltip-${driver.id}-${actor.actor_id}`" class="kb-hover-tooltip-line">{{ normalizeName(actor.name) }}: {{ formatPercent(actor.score) }} ({{ Number(actor.mention_count || 0) }} {{ t('dashboard_geo.overview_geo.table.mentions') }})</div>
						</div>
					</div>
					<div class="legend-list">
						<div v-for="actor in getSortedActors(driver, getFilteredDriver(driver)?.actors)" :key="`${driver.id}-${actor.actor_id}`" :class="['legend-item', { 'is-no-delta': !isRangeComparison }]">
							<span class="legend-name">{{ normalizeName(actor.name) }}</span>
							<span class="legend-value">{{ formatPercent(actor.score) }}</span>
							<span v-if="isRangeComparison" :class="['legend-diff', getDeltaClass(getActorDelta(driver, actor))]">
								{{ formatDeltaPercent(getActorDelta(driver, actor)) }}
							</span>
						</div>
					</div>
				</div>
				<!-- <div v-else class="empty-mini">{{ t('no_data') }}</div> -->
			</div>
		</div>
		<div v-else class="empty-state">{{ t('no_data') }}</div>
	</div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { useDashboardGeoStore } from '@/store/dashboard/dashboardGeo.js';
import { useSubNavigationStore } from '@/store/global/subNavigation';
import CustomCheckBox from '@/components/utilities/customCheckBox.vue';
import RoundSpiderChart from '@/components/utilities/RoundSpiderChart.vue';

const { t } = useI18n();
const maxSelections = 6;

const dashboardStore = useDashboardGeoStore();
const { analysis, last_analysis, selectedDate, labels, keyBenefitLabels } = storeToRefs(dashboardStore);

const subNavigationStore = useSubNavigationStore();
const { selectedAiTypeDashboard } = storeToRefs(subNavigationStore);

const isDropdownOpen = ref(false);
const selectedDriverIds = ref([]);
const hoveredDriverId = ref(null);
const openDriverFilterId = ref(null);
const selectedLabelIdsByDriver = ref({});

const parseIntList = value => {
	if (!Array.isArray(value)) {
		return [];
	}
	return value.map(item => Number(item)).filter(item => Number.isFinite(item));
};

const normalizeBenefitName = value =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.trim()
		.replace(/\s+/g, ' ');

const drivers = computed(() => {
	const aiType = selectedAiTypeDashboard.value;
	return analysis.value?.[aiType]?.drivers_analysis || [];
});

const previousDriversById = computed(() => {
	const aiType = selectedAiTypeDashboard.value;
	const rows = last_analysis.value?.[aiType]?.drivers_analysis || [];
	const map = new Map();
	rows.forEach(driver => {
		map.set(Number(driver?.id), driver);
	});
	return map;
});

const isRangeComparison = computed(() => {
	const range = selectedDate.value || [];
	if (range.length < 2 || !range[0] || !range[1]) {
		return false;
	}
	return new Date(range[0]).getTime() !== new Date(range[1]).getTime();
});

const availableItems = computed(() => {
	const driverItems = drivers.value
		.filter(driver => hasData(driver))
		.map(driver => ({ id: Number(driver.id), name: String(driver.name || '') }))
		.filter(item => Number.isFinite(item.id) && item.name);
	const kbRows = Array.isArray(keyBenefitLabels.value) ? keyBenefitLabels.value : [];
	if (!kbRows.length) {
		return driverItems.sort((a, b) => String(a.name).localeCompare(String(b.name)));
	}

	const allowedKeys = new Set(
		kbRows
			.map(row => normalizeBenefitName(row?.label))
			.filter(Boolean),
	);
	return driverItems
		.filter(item => allowedKeys.has(normalizeBenefitName(item.name)))
		.sort((a, b) => String(a.name).localeCompare(String(b.name)));
});

const selectedDrivers = computed(() => {
	const byId = new Map(drivers.value.map(driver => [Number(driver.id), driver]));
	return selectedDriverIds.value.map(id => byId.get(id)).filter(Boolean);
});

const labelNameById = computed(() => {
	const map = new Map();
	(labels.value || []).forEach(label => {
		const id = Number(label?.id);
		if (Number.isFinite(id)) {
			map.set(id, String(label?.label || `Label ${id}`));
		}
	});
	return map;
});

const normalizeActorRows = actors => {
	const rows = Array.isArray(actors) ? actors : [];
	const seen = new Set();
	const out = [];

	rows.forEach(actor => {
		const actorId = Number(actor?.actor_id ?? 0);
		if (seen.has(actorId)) {
			return;
		}
		seen.add(actorId);
		out.push({
			...actor,
			actor_id: actorId,
			mention_count: Number(actor?.mention_count || 0),
			share: Number(actor?.share || 0),
			score: Number(actor?.score || 0),
			is_client: Boolean(actor?.is_client),
		});
	});

	return out;
};

const sortActors = actors => {
	const rows = normalizeActorRows(actors);
	return [...rows].sort((a, b) => {
		if (a?.is_client && !b?.is_client) return -1;
		if (!a?.is_client && b?.is_client) return 1;
		const scoreDiff = Number(b?.score || 0) - Number(a?.score || 0);
		if (scoreDiff !== 0) return scoreDiff;
		const mentionDiff = Number(b?.mention_count || 0) - Number(a?.mention_count || 0);
		if (mentionDiff !== 0) return mentionDiff;
		return String(a?.name || '').localeCompare(String(b?.name || ''));
	});
};

const getSortedActors = (driver, actors) => normalizeActorRows(actors || []);

const getDriverQuestionRows = driver => {
	const rows = Array.isArray(driver?.question_rows) ? driver.question_rows : [];
	const byRowKey = new Map();

	rows.forEach(row => {
		const questionId = Number(row?.question_id || 0);
		const source = String(row?.source || '');
		const key = `${source}::${questionId}`;
		const labelsInRow = parseIntList(row?.label_ids);
		const actorsInRow = parseIntList(row?.actor_ids);
		const state = byRowKey.get(key) || {
			question_id: questionId,
			source,
			label_ids: new Set(),
			actor_ids: new Set(),
		};

		labelsInRow.forEach(labelId => state.label_ids.add(labelId));
		actorsInRow.forEach(actorId => state.actor_ids.add(actorId));
		byRowKey.set(key, state);
	});

	return [...byRowKey.values()]
		.map(row => ({
			question_id: Number(row.question_id || 0),
			source: String(row.source || ''),
			label_ids: [...row.label_ids].sort((a, b) => a - b),
			actor_ids: [...row.actor_ids].sort((a, b) => a - b),
		}))
		.sort((a, b) => {
			if (a.question_id !== b.question_id) {
				return a.question_id - b.question_id;
			}
			return String(a.source).localeCompare(String(b.source));
		});
};

const getDriverLabelOptions = driver => {
	const labelSet = new Set();
	getDriverQuestionRows(driver).forEach(row => {
		parseIntList(row?.label_ids).forEach(labelId => {
			labelSet.add(labelId);
		});
	});

	return [...labelSet]
		.filter(labelId => labelNameById.value.has(Number(labelId)))
		.map(labelId => ({
			id: Number(labelId),
			name: labelNameById.value.get(Number(labelId)) || `Label ${labelId}`,
		}))
		.sort((a, b) => String(a.name).localeCompare(String(b.name)));
};

const getSelectedDriverLabelIds = driverId => {
	return parseIntList(selectedLabelIdsByDriver.value[String(driverId)]);
};

const isDriverLabelSelected = (driverId, labelId) => {
	return getSelectedDriverLabelIds(driverId).includes(Number(labelId));
};

const handleDriverLabelSelection = (event, driverId, labelId) => {
	const key = String(driverId);
	const current = getSelectedDriverLabelIds(driverId);
	const selected = Boolean(event?.target?.checked);
	const nextSet = new Set(current);

	if (selected) {
		nextSet.add(Number(labelId));
	} else {
		nextSet.delete(Number(labelId));
	}

	selectedLabelIdsByDriver.value = {
		...selectedLabelIdsByDriver.value,
		[key]: [...nextSet].sort((a, b) => a - b),
	};
};

const clearDriverLabelSelection = driverId => {
	const key = String(driverId);
	selectedLabelIdsByDriver.value = {
		...selectedLabelIdsByDriver.value,
		[key]: [],
	};
};

const toggleDriverLabelDropdown = driverId => {
	openDriverFilterId.value = openDriverFilterId.value === driverId ? null : driverId;
};

const getDriverLabelSummary = driver => {
	const selectedCount = getSelectedDriverLabelIds(driver.id).length;
	if (selectedCount === 0) {
		return t('select_labels');
	}
	return t('select_labels') + ` (${selectedCount})`;
};

const computeDriverMetrics = (driver, selectedLabelIds, actorBlueprint = null) => {
	const baseActors = sortActors(actorBlueprint || driver?.actors || []);
	const hasActorBlueprint = Array.isArray(actorBlueprint) && actorBlueprint.length > 0;
	const selectedLabelSet = new Set(parseIntList(selectedLabelIds));
	const rows = driver ? getDriverQuestionRows(driver) : [];
	const filteredRows = rows.filter(row => {
		if (!selectedLabelSet.size) {
			return true;
		}
		const rowLabelIds = parseIntList(row?.label_ids);
		return rowLabelIds.some(labelId => selectedLabelSet.has(labelId));
	});

	const mentionsByActor = new Map();
	filteredRows.forEach(row => {
		const actorSet = new Set(parseIntList(row?.actor_ids));
		actorSet.forEach(actorId => {
			mentionsByActor.set(actorId, Number(mentionsByActor.get(actorId) || 0) + 1);
		});
	});

	const totalOccurrences = filteredRows.length;
	const actorById = new Map();
	baseActors.forEach(actor => {
		actorById.set(Number(actor?.actor_id || 0), actor);
	});
	mentionsByActor.forEach((_, actorId) => {
		if (!actorById.has(Number(actorId))) {
			actorById.set(Number(actorId), {
				actor_id: Number(actorId),
				name: `Actor ${actorId}`,
				color: '#008FFB',
				is_client: Number(actorId) === 0,
				mention_count: 0,
				share: 0,
				score: 0,
			});
		}
	});

	const actors = [...actorById.values()].map(actor => {
		const actorId = Number(actor?.actor_id || 0);
		const mentionCount = Number(mentionsByActor.get(actorId) || 0);
		const share = totalOccurrences > 0 ? mentionCount / totalOccurrences : 0;
		return {
			...actor,
			mention_count: mentionCount,
			share: Number(share.toFixed(4)),
			score: Number((share * 100).toFixed(2)),
		};
	});

	let selectedActors = [];
	if (hasActorBlueprint) {
		const byId = new Map(actors.map(actor => [Number(actor?.actor_id || 0), actor]));
		selectedActors = baseActors.map(actor => {
			const actorId = Number(actor?.actor_id || 0);
			return (
				byId.get(actorId) || {
					...actor,
					mention_count: 0,
					share: 0,
					score: 0,
				}
			);
		});
	} else {
		const sorted = sortActors(actors);
		const clientActor =
			sorted.find(actor => Boolean(actor?.is_client)) || {
				actor_id: 0,
				name: 'Client',
				color: '#1D4ED8',
				is_client: true,
				mention_count: 0,
				share: 0,
				score: 0,
			};
		const nonClientActors = sorted.filter(actor => !Boolean(actor?.is_client)).slice(0, 5);
		selectedActors = [clientActor, ...nonClientActors];
	}

	return {
		total_occurrences: totalOccurrences,
		total_mentions: totalOccurrences,
		actors: normalizeActorRows(selectedActors),
	};
};

const filteredDriversById = computed(() => {
	const map = new Map();
	selectedDrivers.value.forEach(driver => {
		const selectedLabels = getSelectedDriverLabelIds(driver.id);
		map.set(Number(driver.id), computeDriverMetrics(driver, selectedLabels));
	});
	return map;
});

const previousFilteredDriversById = computed(() => {
	const map = new Map();
	selectedDrivers.value.forEach(driver => {
		const driverId = Number(driver.id);
		const selectedLabels = getSelectedDriverLabelIds(driverId);
		const currentMetrics = filteredDriversById.value.get(driverId);
		const actorBlueprint = currentMetrics?.actors || driver?.actors || [];
		const previousDriver = previousDriversById.value.get(driverId);
		map.set(driverId, computeDriverMetrics(previousDriver, selectedLabels, actorBlueprint));
	});
	return map;
});

const getFilteredDriver = driver => {
	const driverId = Number(driver?.id || 0);
	return filteredDriversById.value.get(driverId) || computeDriverMetrics(driver, []);
};

const toggleDropdown = () => {
	isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdown = event => {
	if (!event.target.closest('.driver-selector-dropdown')) {
		isDropdownOpen.value = false;
	}
	if (!event.target.closest('.driver-label-dropdown')) {
		openDriverFilterId.value = null;
	}
};

const handleDriverSelection = (event, driverId) => {
	const selected = Boolean(event?.target?.checked);
	if (selected) {
		if (selectedDriverIds.value.length < maxSelections && !selectedDriverIds.value.includes(driverId)) {
			selectedDriverIds.value = [...selectedDriverIds.value, driverId];
		}
	} else {
		selectedDriverIds.value = selectedDriverIds.value.filter(id => id !== driverId);
	}
};

const normalizeName = value => {
	const raw = String(value || '').trim();
	if (!raw) {
		return '';
	}
	return raw
		.split(' ')
		.map(word =>
			word
				.split('-')
				.map(part => {
					if (!part) return part;
					return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
				})
				.join('-'),
		)
		.join(' ');
};

const hasData = driver => {
	return Number(getFilteredDriver(driver)?.total_occurrences || 0) > 0;
};

const getSpiderScores = driver => {
	const actors = getSortedActors(driver, getFilteredDriver(driver)?.actors || []);
	return actors.map(a => Number(a?.score ?? 0));
};

const getSpiderPreviousScores = driver => {
	if (!isRangeComparison.value) return [];
	const currentActors = getSortedActors(driver, getFilteredDriver(driver)?.actors || []);
	const prevMetrics = previousFilteredDriversById.value.get(Number(driver?.id));
	const prevActors = prevMetrics?.actors || [];
	return currentActors.map(a => {
		const prevActor = prevActors.find(pa => Number(pa?.actor_id) === Number(a?.actor_id));
		return Number(prevActor?.score ?? 0);
	});
};

const formatPercent = score => `${Number(score || 0).toFixed(1)}%`;
const normalizedDeltaPercent = deltaScore => Number(Number(deltaScore || 0).toFixed(1));
const formatDeltaPercent = deltaScore => {
	const value = normalizedDeltaPercent(deltaScore);
	const fixed = Math.abs(value).toFixed(1);
	if (value > 0) return `+${fixed}%`;
	if (value < 0) return `-${fixed}%`;
	return '0.0%';
};
const getDeltaClass = deltaScore => {
	const value = normalizedDeltaPercent(deltaScore);
	if (value > 0) return 'is-positive';
	if (value < 0) return 'is-negative';
	return 'is-neutral';
};
const getActorDelta = (driver, actor) => {
	const driverId = Number(driver?.id || 0);
	const actorId = Number(actor?.actor_id || 0);
	const currentMetrics = filteredDriversById.value.get(driverId);
	const previousMetrics = previousFilteredDriversById.value.get(driverId);

	const currentActor = (currentMetrics?.actors || []).find(row => Number(row?.actor_id || 0) === actorId);
	const previousActor = (previousMetrics?.actors || []).find(row => Number(row?.actor_id || 0) === actorId);
	const current = Number(currentActor?.score || 0);
	const previous = Number(previousActor?.score || 0);
	return current - previous;
};

watch(
	availableItems,
	items => {
		const validIds = new Set(items.map(item => item.id));
		const cleaned = selectedDriverIds.value.filter(id => validIds.has(id));
		if (cleaned.length !== selectedDriverIds.value.length) {
			selectedDriverIds.value = cleaned;
		}
		if (selectedDriverIds.value.length === 0 && items.length > 0) {
			selectedDriverIds.value = items.slice(0, maxSelections).map(item => item.id);
		}
	},
	{ immediate: true },
);

watch(
	selectedDrivers,
	driversRows => {
		const nextSelectionByDriver = {};
		const selectedDriverIdsSet = new Set(driversRows.map(driver => Number(driver.id)));

		driversRows.forEach(driver => {
			const driverId = Number(driver.id);
			const validLabelIds = new Set(getDriverLabelOptions(driver).map(item => Number(item.id)));
			const preserved = getSelectedDriverLabelIds(driverId).filter(labelId => validLabelIds.has(Number(labelId)));
			nextSelectionByDriver[String(driverId)] = preserved;
		});

		selectedLabelIdsByDriver.value = nextSelectionByDriver;

		if (openDriverFilterId.value !== null && !selectedDriverIdsSet.has(Number(openDriverFilterId.value))) {
			openDriverFilterId.value = null;
		}
	},
	{ immediate: true, deep: true },
);

onMounted(() => {
	document.addEventListener('click', closeDropdown);
});

onUnmounted(() => {
	document.removeEventListener('click', closeDropdown);
});
</script>

<style lang="scss" scoped>
.key-benefit-analysis {
	padding: 0.6rem;

	.analysis-header {
		display: flex;
		justify-content: flex-end;
	}

	.selector {
		.driver-selector-dropdown {
			position: relative;

			.dropdown-trigger {
				display: flex;
				align-items: center;
				padding: 0.8rem 1.2rem;
				border: 2px solid var(--gris-100);
				border-radius: 8px;
				background: var(--blanc);
				cursor: pointer;
				font-size: 0.9rem;
			}

			&.is-active .dropdown-trigger {
				border-color: var(--bleu-200);
			}

			.dropdown-menu {
				position: absolute;
				top: 100%;
				right: 0;
				z-index: 1000;
				min-width: 300px;
				margin-top: 4px;
				opacity: 0;
				visibility: hidden;
				transform: translateY(-8px);
				transition: all 0.2s;

				.dropdown-content {
					background: var(--blanc);
					border: 1px solid var(--gris-100);
					border-radius: 8px;
					max-height: 360px;
					overflow: auto;
					padding: 0.4rem 0.8rem;

					p {
						margin-bottom: 0.4rem;
					}

					.dropdown-items {
						display: flex;
						flex-direction: column;
						gap: 0.3rem;
					}

					.dropdown-item.checkbox {
						display: flex;
						flex-direction: row;
						align-items: center;
						gap: 0.5rem;
						cursor: pointer;

						.dropdown-item-label {
							min-width: 0;
							overflow-wrap: break-word;
						}
					}
				}
			}

			&.is-active .dropdown-menu {
				opacity: 1;
				visibility: visible;
				transform: translateY(0);
			}
		}
	}

	.chart-grid {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 1rem 2rem;
		margin-top: 1rem;
	}

	.chart-card {
		padding: 0.8rem;
		border-radius: 10px;
		background: var(--blanc);
	}

	.chart-header {
		display: grid;
		grid-template-columns: 1fr auto 1fr;
		align-items: center;
		gap: 0.5rem;
		margin-bottom: 0.4rem;

		.chart-title {
			grid-column: 2;
			justify-self: center;
			font-size: 1.05rem;
		}

		.driver-filter {
			grid-column: 3;
			justify-self: end;
		}
	}

	.driver-filter {
		position: relative;
	}

	.driver-label-dropdown {
		position: relative;

		.driver-label-trigger {
			background: var(--blanc);
			border: 1px solid var(--gris-100);
			border-radius: 6px;
			padding: 0.35rem 0.55rem;
			font-size: 0.8rem;
			cursor: pointer;
			white-space: nowrap;
		}

		&.is-active .driver-label-trigger {
			border-color: var(--bleu-200);
		}

		.driver-label-menu {
			position: absolute;
			top: calc(100% + 4px);
			right: 0;
			z-index: 1100;
			min-width: 250px;
			max-width: 320px;
			max-height: 280px;
			overflow: auto;
			background: var(--blanc);
			border: 1px solid var(--gris-100);
			border-radius: 8px;
			padding: 0.5rem 0.6rem;
			opacity: 0;
			visibility: hidden;
			transform: translateY(-6px);
			transition: all 0.2s;
		}

		&.is-active .driver-label-menu {
			opacity: 1;
			visibility: visible;
			transform: translateY(0);
		}
	}

	.driver-label-helper {
		font-size: 0.8rem;
		color: var(--gris-500);
	}

	.driver-label-options {
		display: flex;
		flex-direction: column;
		gap: 0.3rem;
		max-height: 220px;
		overflow: auto;
	}

	.driver-label-checkbox {
		display: flex;
		align-items: flex-start;
		gap: 0.5rem;
		cursor: pointer;
		min-width: 0;

		> *:first-child {
			flex-shrink: 0;
		}

		.driver-label-text {
			flex: 1;
			min-width: 0;
			word-wrap: break-word;
			overflow-wrap: break-word;
			white-space: normal;
		}
	}

	.driver-label-clear {
		margin-top: 0.45rem;
		background: transparent;
		border: 1px solid var(--gris-200);
		border-radius: 6px;
		padding: 0.25rem 0.45rem;
		font-size: 0.76rem;
		cursor: pointer;
	}
	
	.chart-content {
		display: flex;
		gap: 1rem;
		align-items: center;
	}

	.chart-wrapper {
		flex: 0 0 56%;
		position: relative;
		min-height: 360px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.chart-canvas {
		width: 100%;
		height: 330px;
	}

	.chart-canvas {
		:deep(.round-spider-chart),
		:deep(.spider-svg) {
			width: 100%;
			height: 100%;
		}
	}

	.kb-hover-tooltip {
		position: absolute;
		left: 0.8rem;
		top: 0.8rem;
		z-index: 6;
		padding: 0.5rem 0.6rem;
		background: var(--blanc);
		border: 1px solid var(--gris-100);
		border-radius: .5rem;
		max-width: 340px;
		pointer-events: none;
	}

	.kb-hover-tooltip-title {
		font-weight: 700;
		color: var(--gris-900);
		margin-bottom: 0.5rem;
	}

	.kb-hover-tooltip-line {
		font-size: 0.82rem;
		color: var(--gris-800);
		line-height: 1.35;
	}

	.legend-list {
		flex: 1;
		max-height: 320px;
		overflow: auto;
		overflow-x: hidden;
		padding: 2rem 0;
		padding-right: 0.5rem;
		display: grid;
		grid-template-columns: minmax(0, 160px) auto auto;
		gap: 0.2rem;
		align-content: start;
		min-width: 0;
	}
	
	.legend-item {
		display: grid;
		grid-column: 1 / -1;
		grid-template-columns: subgrid;
		align-items: center;
		font-size: 0.9rem;
		min-width: 0;
	}
	
	.legend-item.is-no-delta {
		grid-template-columns: subgrid;
	}
	
	.legend-name {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		padding: 0.2rem 0;
		color: var(--gris-700);
	}
	
	.legend-value {
		font-weight: 600;
		color: var(--gris-900);
		text-align: right;
		white-space: nowrap;
		justify-self: end;
		padding-right: .5rem;
	}

	.legend-diff {
		font-weight: 600;
		justify-self: end;
		text-align: right;
		white-space: nowrap;
	}

	.legend-diff.is-positive {
		color: var(--vert-500);
	}

	.legend-diff.is-negative {
		color: var(--rouge-500);
	}

	.legend-diff.is-neutral {
		color: var(--gris-400);
	}

	.empty-mini {
		padding: 2rem 0;
		text-align: center;
		color: var(--gris-500);
		font-style: italic;
	}

	.empty-state {
		padding: 2.5rem 1rem;
		text-align: center;
		color: var(--gris-600);
	}
}

@media (max-width: 1200px) {
	.key-benefit-analysis .chart-grid {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 768px) {
	.key-benefit-analysis .chart-content {
		flex-direction: column;
		align-items: stretch;
	}

	.key-benefit-analysis .chart-wrapper {
		flex: auto;
	}

	.key-benefit-analysis .chart-header {
		grid-template-columns: 1fr;
		grid-template-rows: auto auto;

		.chart-title {
			grid-column: 1;
			grid-row: 1;
		}

		.driver-filter {
			grid-column: 1;
			grid-row: 2;
			justify-self: end;
		}
	}
}
</style>
